import * as React from "react";
import styled from "styled-components";
import throttle from "lodash/throttle";
import { detect } from "detect-browser";

import { animatedScrollTo } from "../../lib/animate";
import { ScreenSize, isLarger } from "../utils/media";
import WithScreenSize, { ScreenSizeProps } from "../../common/components/WithScreenSize";

const browser = detect();

export interface NavElementProps {
  sectionIds: string[];
  currentSectionId: string;
  sectionNamesMap: { [sectionId: string]: string };
  wrapperRef: HTMLElement | null;
  onClick: (sectionId: string) => void;
}

type NavElementFunc = (props: NavElementProps) => React.ReactNode;

interface OwnProps {
  renderNav: NavElementFunc;
  sectionClassId?: string;
  blockAlign?: "center" | "start";
}

interface State {
  currentSectionId: string;
}

type Props = OwnProps & ScreenSizeProps;

class SectionNavigator extends React.Component<Props, State> {
  public static defaultProps: Partial<Props> = {
    sectionClassId: "navigatable-section",
    blockAlign: "center",
  };

  private sections: Array<{ id: string; elem: HTMLElement; displayName: string }>;
  private base: HTMLElement | null = null;
  private listeningScroll: boolean;
  private onScrollThrottled: () => void;

  constructor(props: Props) {
    super(props);
    this.sections = [];
    this.listeningScroll = false;
    this.state = {
      currentSectionId: "",
    };
    this.onScrollThrottled = throttle(this.onScroll, 50);
  }

  public componentDidMount() {
    const sectionElems = document.getElementsByClassName(this.props.sectionClassId!);
    if (!sectionElems.length) {
      return;
    }
    this.sections = Array.prototype.slice
      .call(sectionElems)
      .map((e: HTMLElement) => ({ id: e.id, elem: e, displayName: e.getAttribute("data-display-name")! }));

    this.setState({ currentSectionId: this.sections[0].id });
    this.listenScrollIfWideView(this.props.screenSize);
  }

  public componentWillReceiveProps(nextProps: Props) {
    if (isLarger(nextProps.screenSize, this.props.screenSize)) {
      this.listenScrollIfWideView(nextProps.screenSize!);
    }
  }

  public componentWillUnmount() {
    window.removeEventListener("scroll", this.onScrollThrottled);
  }

  public listenScrollIfWideView(screenSize: ScreenSize) {
    if (this.listeningScroll) {
      return;
    }
    if (isLarger(screenSize, "large", true)) {
      window.addEventListener("scroll", this.onScrollThrottled);
      this.listeningScroll = true;
    }
  }

  public onScroll = () => {
    const currentSectionId = this.calcCurrentSection();
    this.setState({ currentSectionId });
  };

  public calcCurrentSection() {
    const halfWindowHeight = window.innerHeight / 2;
    const target = this.sections.reduce(
      (acc, section) => {
        const rect = section.elem.getBoundingClientRect();
        const minDist = Math.min(Math.abs(rect.top - halfWindowHeight), Math.abs(rect.bottom - 1 - halfWindowHeight));
        if (minDist < acc.min) {
          return { min: minDist, id: section.id };
        }
        return acc;
      },
      { min: 10000, id: "" }
    );
    return target.id;
  }

  public onClickNavItem = (sectionId: string) => {
    const section = this.sections.find(s => s.id === sectionId)!;
    const signedDistance = section.elem.getBoundingClientRect().top;
    const smoothScroll = Math.abs(signedDistance) < 4000;
    if (section) {
      if (browser && (browser.name === "chrome" || browser.name === "firefox")) {
        section.elem.scrollIntoView({ behavior: smoothScroll ? "smooth" : "instant", block: this.props.blockAlign });
      } else {
        let targetOffset = window.pageYOffset + signedDistance;
        if (this.props.blockAlign === "center") {
          targetOffset -= (window.innerHeight - section.elem.clientHeight) / 2;
        }
        if (smoothScroll) {
          animatedScrollTo(targetOffset, 350);
        } else {
          window.scrollTo(window.pageXOffset, targetOffset);
        }
      }
    }
  };

  public render() {
    const { renderNav, children } = this.props;
    const { currentSectionId } = this.state;
    const sectionIds = this.sections.map(s => s.id);
    const sectionNamesMap = this.sections.reduce(
      (acc, section) => {
        acc[section.id] = section.displayName;
        return acc;
      },
      {} as { [sectionId: string]: string }
    );

    return (
      <Base innerRef={el => (this.base = el)}>
        {renderNav({
          sectionIds,
          currentSectionId,
          sectionNamesMap,
          wrapperRef: this.base,
          onClick: this.onClickNavItem,
        })}
        {children}
      </Base>
    );
  }
}

const Base = styled.div`
  position: relative;
`;

type SectionProps = {
  id: string;
  displayName: string;
  classId?: string;
  className?: string;
} & React.HTMLAttributes<any>;

export const NavigatableSection: React.SFC<SectionProps> = ({
  id,
  displayName,
  classId = "navigatable-section",
  className,
  children,
  ...rest
}) => (
  <section id={id} data-display-name={displayName} className={`${className} ${classId}`} {...rest}>
    {children}
  </section>
);

const SectionNavigatorWithScreenSize: React.SFC<OwnProps> = props => {
  return <WithScreenSize>{({ screenSize }) => <SectionNavigator {...props} screenSize={screenSize} />}</WithScreenSize>;
};
export default SectionNavigatorWithScreenSize;



// WEBPACK FOOTER //
// ./frontend/src/common/components/SectionNavigator.tsx